import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { Stack } from '@mui/material';

import {
	PrimaryButton,
	RED_COLOR_SX,
} from 'src/components/common/buttons/PrimaryButton';
import { ConfirmationModal } from 'src/components/common/modal/ConfirmationModal';
import { IStudyAssignmentActionsProps } from 'src/features/annotation/domain/types';
import { StudyAssignmentStatus } from 'src/features/study_assignment/domain/types';
import { STUDY_ASSIGNMENT_LOADING_KEY } from 'src/features/study_assignment/store';
import { RootState } from 'src/store';

const StudyAssignmentActions: React.FC<IStudyAssignmentActionsProps> = ({
	studyAssignmentStatus,
	isAssignedUserViewing,
	isAdminUserViewing,
	onUpdateStatus,
	hasNextAssignment = false,
	onNextAssignment,
}) => {
	const isLoading = useSelector(
		(state: RootState) =>
			state.loading.loadingStates[STUDY_ASSIGNMENT_LOADING_KEY],
	);

	const [confirmationModal, setConfirmationModal] = useState<{
		open: boolean;
		status: StudyAssignmentStatus;
	} | null>(null);

	const buttonsToRender: {
		text: string;
		action: () => void;
		key: string;
		red?: boolean;
	}[] = [];

	switch (studyAssignmentStatus) {
		// case 'unassigned':
		// 	if (isAdminUserViewing) {
		// 		buttonsToRender.push({
		// 			text: 'Mark as Ungradable',
		// 			action: () => onUpdateStatus('ungradable'),
		// 			key: 'ungradable',
		// 			red: true,
		// 		});
		// 	}
		// 	break;

		case 'active':
			if (isAssignedUserViewing) {
				buttonsToRender.push({
					text: 'Start Annotation',
					action: () => onUpdateStatus('in_progress'),
					key: 'start-annotation',
				});
				buttonsToRender.push({
					text: 'Mark Study as Ungradable',
					action: () =>
						setConfirmationModal({
							open: true,
							status: 'ungradable',
						}),
					key: 'ungradable',
					red: true,
				});
			}
			break;

		case 'in_progress':
			if (isAssignedUserViewing) {
				buttonsToRender.push({
					text: 'Mark as Completed',
					action: () =>
						setConfirmationModal({
							open: true,
							status: 'completed',
						}),
					key: 'completed',
				});

				buttonsToRender.push({
					text: 'Mark Study as Ungradable',
					action: () =>
						setConfirmationModal({
							open: true,
							status: 'ungradable',
						}),
					key: 'ungradable',
					red: true,
				});
			}
			break;

		case 'completed':
		case 'ungradable':
			if (isAssignedUserViewing && hasNextAssignment) {
				buttonsToRender.push({
					text: 'Next Assignment',
					action: () => onNextAssignment?.(),
					key: 'next-assignment',
				});
			}
			break;

		case 'canceled':
			break;

		default:
			break;
	}

	if (buttonsToRender.length === 0) return null;

	return (
		<Stack direction='row' spacing={2}>
			{buttonsToRender.map((btn) => (
				<PrimaryButton
					key={btn.key}
					size='medium'
					fullWidth={false}
					sx={{
						height: '40px',
						...(btn.red ? RED_COLOR_SX : {}),
					}}
					onClick={btn.action}
					disabled={isLoading}
				>
					{btn.text}
				</PrimaryButton>
			))}

			{confirmationModal?.status && (
				<ConfirmationModal
					title={
						<>
							Are you sure you want to mark study as{' '}
							{confirmationModal?.status}?
						</>
					}
					modalContent={'This action cannot be undone.'}
					modalOpen={confirmationModal?.open ?? false}
					handleYes={() => {
						onUpdateStatus(confirmationModal?.status, () => {
							setConfirmationModal(null);
						});
					}}
					loadingKey={STUDY_ASSIGNMENT_LOADING_KEY}
					handleNo={() => {
						setConfirmationModal(null);
					}}
					width='480px'
				/>
			)}
		</Stack>
	);
};

export default StudyAssignmentActions;
